import React, { useState, FormEvent } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiPost } from '../api/client';
import type { TwoFactorLoginRequest, LoginResponse } from '../types/api';
import AlertMessage from '../components/AlertMessage';
import ReportProblemLink from '../components/ReportProblemLink';
import LoadingSpinner from '../components/LoadingSpinner';
import './TwoFactorLoginPage.css';

export default function TwoFactorLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as any)?.email || '';

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!email) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Session expired</p>
          <div className="divider" />
          <AlertMessage type="error" message="Please sign in again." />
          <div className="auth-links">
            <Link to="/login">Go to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const body: TwoFactorLoginRequest = { email, code, useRecoveryCode: false };
      const response = await apiPost<LoginResponse>('/api/auth/2fa/login', body);
      login(response);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1 className="auth-title">Cedar Vault</h1>
        <p className="auth-subtitle">Enter your Cedar Vault 6-digit code</p>
        <div className="divider" />

        <AlertMessage type="error" message={error} />

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="tfaCode">Verification code</label>
            <input
              id="tfaCode"
              className="form-input tfa-code-input"
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="000000"
              required
              autoComplete="one-time-code"
              autoFocus
            />
          </div>

          <button className="btn-primary" type="submit" disabled={loading}>
            {loading ? <LoadingSpinner small /> : 'Verify'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/login">Back to sign in</Link>
        </div>
        <p className="auth-site-link"><ReportProblemLink /></p>
      </div>
    </div>
  );
}
